// Keycloak OIDC/PKCE operations.
// Ported from the old portal's auth.js — no npm dependencies.

import type { AuthUser } from '@/stores/auth';
import { generateRandomString, generateCodeChallenge } from './pkce';
import { tokenStorage, parseJwt, isTokenExpired, getStoredTokens } from './token';

// ── Config (from Vite env vars, baked at build time) ──

const AUTH_ISSUER = import.meta.env.VITE_AUTH_ISSUER ?? '';
const AUTH_CLIENT_ID = import.meta.env.VITE_AUTH_CLIENT_ID ?? 'installer-portal';
const AUTH_SCOPES = import.meta.env.VITE_AUTH_SCOPES ?? 'openid profile email roles';

function getRedirectUri(): string {
  const base = import.meta.env.BASE_URL ?? '/';
  return window.location.origin + base + 'callback';
}

function getPostLogoutUri(): string {
  const base = import.meta.env.BASE_URL ?? '/';
  return window.location.origin + base;
}

// ── OIDC Discovery ──

interface OidcEndpoints {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint: string;
}

let _endpoints: OidcEndpoints | null = null;

export async function getEndpoints(): Promise<OidcEndpoints> {
  if (_endpoints) return _endpoints;
  const res = await fetch(`${AUTH_ISSUER}/.well-known/openid-configuration`);
  if (!res.ok) throw new Error('Kan Keycloak niet bereiken');
  _endpoints = await res.json();
  return _endpoints!;
}

// ── Login ──

export async function login(): Promise<void> {
  const endpoints = await getEndpoints();

  const verifier = generateRandomString(64);
  const challenge = await generateCodeChallenge(verifier);
  tokenStorage.set('code_verifier', verifier);

  const state = generateRandomString(16);
  tokenStorage.set('auth_state', state);

  // Save current URL so we can restore it after login
  sessionStorage.setItem(
    'auth_redirect_url',
    window.location.pathname + window.location.search,
  );

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: AUTH_CLIENT_ID,
    redirect_uri: getRedirectUri(),
    scope: AUTH_SCOPES,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  window.location.href = `${endpoints.authorization_endpoint}?${params}`;
}

// ── Callback ──

export interface CallbackResult {
  accessToken: string;
  idToken: string;
  refreshToken: string | null;
  redirectUrl: string;
}

export async function handleCallback(): Promise<CallbackResult> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const returnedState = params.get('state');
  const error = params.get('error');

  if (error) {
    throw new Error(params.get('error_description') ?? error);
  }

  const savedState = tokenStorage.get('auth_state');
  if (!code || returnedState !== savedState) {
    throw new Error('State mismatch — mogelijk CSRF, login afgebroken');
  }

  const verifier = tokenStorage.get('code_verifier');
  if (!verifier) {
    throw new Error('PKCE code_verifier ontbreekt');
  }

  const endpoints = await getEndpoints();

  const response = await fetch(endpoints.token_endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: AUTH_CLIENT_ID,
      redirect_uri: getRedirectUri(),
      code,
      code_verifier: verifier,
    }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Token exchange mislukt: ${text}`);
  }

  const tokens = await response.json();

  // Store tokens
  tokenStorage.set('access_token', tokens.access_token);
  tokenStorage.set('id_token', tokens.id_token);
  if (tokens.refresh_token) {
    tokenStorage.set('refresh_token', tokens.refresh_token);
  }

  // Clean up PKCE state
  tokenStorage.remove('code_verifier');
  tokenStorage.remove('auth_state');

  // Get saved redirect URL
  const redirectUrl = sessionStorage.getItem('auth_redirect_url') ?? '/';
  sessionStorage.removeItem('auth_redirect_url');

  return {
    accessToken: tokens.access_token,
    idToken: tokens.id_token,
    refreshToken: tokens.refresh_token ?? null,
    redirectUrl,
  };
}

// ── Token Refresh ──

let _refreshing: Promise<boolean> | null = null;

export async function refreshAccessToken(): Promise<boolean> {
  // Deduplicate: if a refresh is already in-flight, wait for it
  if (_refreshing) return _refreshing;

  _refreshing = (async () => {
    const refreshToken = tokenStorage.get('refresh_token');
    if (!refreshToken) return false;

    try {
      const endpoints = await getEndpoints();
      const res = await fetch(endpoints.token_endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: AUTH_CLIENT_ID,
          refresh_token: refreshToken,
        }),
      });

      if (!res.ok) return false;

      const tokens = await res.json();
      tokenStorage.set('access_token', tokens.access_token);
      if (tokens.id_token) tokenStorage.set('id_token', tokens.id_token);
      if (tokens.refresh_token) tokenStorage.set('refresh_token', tokens.refresh_token);
      return true;
    } catch {
      return false;
    }
  })();

  try {
    return await _refreshing;
  } finally {
    _refreshing = null;
  }
}

export async function ensureValidToken(): Promise<boolean> {
  if (!isTokenExpired()) return true;
  return refreshAccessToken();
}

// ── Logout ──

export async function logout(): Promise<void> {
  const idToken = tokenStorage.get('id_token');
  tokenStorage.clearTokens();

  try {
    const endpoints = await getEndpoints();
    const params = new URLSearchParams({
      post_logout_redirect_uri: getPostLogoutUri(),
      client_id: AUTH_CLIENT_ID,
    });
    if (idToken) params.set('id_token_hint', idToken);
    window.location.href = `${endpoints.end_session_endpoint}?${params}`;
  } catch {
    // If Keycloak is unreachable, just redirect to root
    window.location.href = getPostLogoutUri();
  }
}

// ── User extraction ──

const FILTERED_ROLES = ['offline_access', 'uma_authorization'];

export function extractUser(accessToken: string, idToken: string): AuthUser {
  const access = parseJwt(accessToken);
  const id = parseJwt(idToken);

  // Roles from access_token — filter out Keycloak built-in roles
  const allRoles = access?.roles ?? access?.realm_access?.roles ?? [];
  const roles = allRoles.filter((r) => {
    const lower = r.toLowerCase();
    return !FILTERED_ROLES.includes(lower) && !lower.startsWith('default-roles-');
  });

  const rawOdooId = access?.odoo_id;
  const odooId = typeof rawOdooId === 'number'
    ? rawOdooId
    : typeof rawOdooId === 'string'
      ? Number(rawOdooId) || null
      : null;

  return {
    id: access?.sub ?? id?.sub ?? '',
    name: id?.name ?? access?.name ?? '',
    email: id?.email ?? access?.email ?? '',
    roles,
    odooId,
  };
}
